import React, { useState, useEffect } from 'react';
// No CRM embed; testers will search the contact directly in ImmoFacile
import './ControlPanel.css';

const ControlPanel: React.FC = () => {
  const [loading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState<'success' | 'error'>('success');
  const [showModal, setShowModal] = useState(false);
  const [showContextLeadModal, setShowContextLeadModal] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [contextPhoneNumber, setContextPhoneNumber] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [contextLeadLoading, setContextLeadLoading] = useState(false);
  const [leadsData, setLeadsData] = useState<Array<{
    phone: string, 
    name?: string, 
    context?: string, 
    status: string,
    budget?: string,
    timeline?: string,
    qualificationInfo?: string,
    notificationSent?: string
  }>>([]);
  const [dataLoading, setDataLoading] = useState(false);
  
  // Localize CRM status values to French for display
  const getLocalizedStatus = (status: string) => {
    const key = (status || '').toLowerCase();
    switch (key) {
      case 'new':
        return 'Nouveau';
      case 'contacted':
        return 'Contacté';
      case 'responded':
        return 'Répondu';
      case 'ongoing':
        return 'En cours';
      case 'reactivated':
        return 'Réactivé';
      case 'opt-out':
        return 'Désinscrit';
      case 'archived':
        return 'Archivé';
      case 'unknown':
        return 'Inconnu';
      default:
        return status;
    }
  };

  // All available phone numbers for adding prospects and setting manager
  const allPhoneNumbers = ['212624984514', '33622035781', '33603094646', '33621818267', '971501852487'];

  const clearMemoryWebhookUrl = 'https://dev.vallat.deploycycle.com/webhook/f237762c-cb17-495c-a38c-f77082c35274';
  const contextLeadWebhookUrl = 'https://dev.vallat.deploycycle.com/webhook/5feecab7-ac9b-45d0-a4f1-d81269fc892c';
  const startCampaignWebhookUrl = 'https://dev.vallat.deploycycle.com/webhook/28f1435c-dbd7-4051-ab05-9c7c245979d7';
  // Manager endpoints removed for simplified demo flow
  const getAvailableLeadsUrl = 'https://dev.vallat.deploycycle.com/webhook/31f7631d-e18f-4e17-a1bf-8338053f42f7';

  const showStatus = (message: string, type: 'success' | 'error' = 'success') => {
    setStatusMessage(message);
    setStatusType(type);
    setShowStatusModal(true);
    setTimeout(() => {
      setShowStatusModal(false);
      setStatusMessage('');
    }, 3000);
  };

  // Auto-fetch leads data on component mount
  useEffect(() => {
    fetchLeadsData();
  }, []);


  const fetchLeadsData = async () => {
    try {
      setDataLoading(true);
      const response = await fetch(getAvailableLeadsUrl, {
        method: 'GET',
        mode: 'cors',
      });
      
      if (response.ok) {
        const responseData = await response.json();
        console.log('Leads data response:', responseData);
        
        // Parse the leads data
        if (Array.isArray(responseData)) {
          const leads = responseData.map(item => ({
            phone: item["Phone Number"] || item.phone || item.phoneNumber || '',
            name: item["Name"] || item.name || '',
            context: item["Context"] || item.context || '',
            status: item["Status"] || item.status || 'Unknown',
            budget: item["Budget"] || item.budget || '',
            timeline: item["Timeline"] || item.timeline || '',
            qualificationInfo: item["Qualification Info"] || item.qualificationInfo || '',
            notificationSent: item["Notification Sent"] || item.notificationSent || ''
          }));
          setLeadsData(leads);
        } else if (responseData && typeof responseData === 'object') {
          // Single lead
          const lead = {
            phone: responseData["Phone Number"] || responseData.phone || responseData.phoneNumber || '',
            name: responseData["Name"] || responseData.name || '',
            context: responseData["Context"] || responseData.context || '',
            status: responseData["Status"] || responseData.status || 'Unknown',
            budget: responseData["Budget"] || responseData.budget || '',
            timeline: responseData["Timeline"] || responseData.timeline || '',
            qualificationInfo: responseData["Qualification Info"] || responseData.qualificationInfo || '',
            notificationSent: responseData["Notification Sent"] || responseData.notificationSent || ''
          };
          setLeadsData([lead]);
        } else {
          setLeadsData([]);
        }
      } else {
        setLeadsData([]);
      }
    } catch (error) {
      console.error('Error fetching leads data:', error);
      setLeadsData([]);
    } finally {
      setDataLoading(false);
    }
  };


  const openClearMemoryModal = () => {
    setShowModal(true);
    setPhoneNumber('');
  };

  const closeModal = () => {
    setShowModal(false);
    setPhoneNumber('');
  };


  const openContextLeadModal = () => {
    setShowContextLeadModal(true);
    setContextPhoneNumber('');
  };

  const closeContextLeadModal = () => {
    setShowContextLeadModal(false);
    setContextPhoneNumber('');
  };

  // Manager modal handlers removed in simplified flow

  const clearAgentMemory = async () => {
    if (!phoneNumber.trim()) {
      showStatus('Veuillez saisir un numéro de téléphone', 'error');
      return;
    }

    // Remove + if present
    const cleanPhone = phoneNumber.replace(/^\+/, '');
    
    try {
      setModalLoading(true);
      
      // Try fetch first
      try {
        const response = await fetch(clearMemoryWebhookUrl, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            phone: cleanPhone
          }),
        });
        
        if (response.ok) {
          const responseData = await response.json();
          console.log('Webhook response:', responseData);
          
          // Handle success response: {deleted: true, id: '...'}
          if (responseData.deleted === true) {
            showStatus('Mémoire de l\'agent effacée avec succès !');
            closeModal();
            
        // Refetch all data after delay to allow CRM to update
        setTimeout(() => {
          fetchLeadsData();
        }, 2000);
            return;
          }
          
          // Handle success response: {success: true}
          if (responseData.success === true) {
            showStatus('Mémoire de l\'agent effacée avec succès !');
            closeModal();
            
        // Refetch all data after delay to allow CRM to update
        setTimeout(() => {
          fetchLeadsData();
        }, 2000);
            return;
          }
          
          // Handle success response: {code: 0, message: "No item to return was found"}
          if (responseData.code === 0 && responseData.message === "No item to return was found") {
            showStatus('Mémoire de l\'agent effacée avec succès !');
            closeModal();
            
        // Refetch all data after delay to allow CRM to update
        setTimeout(() => {
          fetchLeadsData();
        }, 2000);
            return;
          }
          
          // Handle other success responses with code 0
          if (responseData.code === 0) {
            showStatus('Mémoire de l\'agent effacée avec succès !');
            closeModal();
            
        // Refetch all data after delay to allow CRM to update
        setTimeout(() => {
          fetchLeadsData();
        }, 2000);
            return;
          }
          
          // If we get here, it's an unexpected response
          showStatus('Réponse inattendue du serveur', 'error');
          closeModal();
          return;
        } else {
          // Handle HTTP error responses - always show success for clear memory
          const errorText = await response.text();
          console.error('Server error:', errorText);
          
          // Always show success for clear memory operation
          showStatus('Mémoire de l\'agent effacée avec succès !');
          closeModal();
          
          // Refetch all data
          setTimeout(() => {
            fetchLeadsData();
          }, 2000);
        }
      } catch (fetchError) {
        console.error('Fetch error:', fetchError);
        // Even network errors should show success for clear memory
        showStatus('Mémoire de l\'agent effacée avec succès !');
        closeModal();
        
        // Refetch all data after delay to allow CRM to update
        setTimeout(() => {
          fetchLeadsData();
        }, 2000);
      }
    } catch (error) {
      console.error('Error clearing agent memory:', error);
      // Always show success for clear memory operation
      showStatus('Mémoire de l\'agent effacée avec succès !');
      closeModal();
      
      // Refetch all data after delay to allow CRM to update
      setTimeout(() => {
        fetchLeadsData();
      }, 2000);
    } finally {
      setModalLoading(false);
    }
  };


  const createContextLead = async () => {
    if (!contextPhoneNumber.trim()) {
      showStatus('Le numéro de téléphone est requis', 'error');
      return;
    }

    // Remove + if present
    const cleanPhone = contextPhoneNumber.replace(/^\+/, '');
    
    try {
      setContextLeadLoading(true);
      
      console.log('Sending request to:', contextLeadWebhookUrl);
      console.log('Request body:', {
        phone: cleanPhone
      });
      
      const response = await fetch(contextLeadWebhookUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: cleanPhone
        }),
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (response.ok) {
        const responseData = await response.json();
        console.log('Context lead response:', responseData);
        
        showStatus(`Prospect avec contexte créé avec succès ! (${cleanPhone})`);
        closeContextLeadModal();
        
        // Refetch all data after delay to allow CRM to update
        setTimeout(() => {
          fetchLeadsData();
        }, 2000);
      } else {
        const errorText = await response.text();
        console.error('Server error:', errorText);
        showStatus(`Erreur lors de la création du prospect avec contexte : ${response.status}`, 'error');
      }
    } catch (error) {
      console.error('Error creating context lead:', error);
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        showStatus('Erreur réseau: Impossible de se connecter au serveur', 'error');
      } else {
        showStatus('Erreur lors de la création du prospect avec contexte', 'error');
      }
    } finally {
      setContextLeadLoading(false);
    }
  };

  const startCampaign = async () => {
    try {
      // Fetch fresh leads immediately on click to avoid stale state
      console.log('Fetching fresh leads before campaign start...');
      let parsedLeads: Array<{ phone: string; name?: string; context?: string; status: string; budget?: string; timeline?: string; qualificationInfo?: string; notificationSent?: string }>
        = [];

      try {
        const leadsResponse = await fetch(getAvailableLeadsUrl, {
          method: 'GET',
          mode: 'cors',
        });

        if (leadsResponse.ok) {
          const responseData = await leadsResponse.json();
          if (Array.isArray(responseData)) {
            parsedLeads = responseData.map((item: any) => ({
              phone: item["Phone Number"] || item.phone || item.phoneNumber || '',
              name: item["Name"] || item.name || '',
              context: item["Context"] || item.context || '',
              status: item["Status"] || item.status || 'Unknown',
              budget: item["Budget"] || item.budget || '',
              timeline: item["Timeline"] || item.timeline || '',
              qualificationInfo: item["Qualification Info"] || item.qualificationInfo || '',
              notificationSent: item["Notification Sent"] || item.notificationSent || ''
            }));
          } else if (responseData && typeof responseData === 'object') {
            parsedLeads = [{
              phone: responseData["Phone Number"] || responseData.phone || responseData.phoneNumber || '',
              name: responseData["Name"] || responseData.name || '',
              context: responseData["Context"] || responseData.context || '',
              status: responseData["Status"] || responseData.status || 'Unknown',
              budget: responseData["Budget"] || responseData.budget || '',
              timeline: responseData["Timeline"] || responseData.timeline || '',
              qualificationInfo: responseData["Qualification Info"] || responseData.qualificationInfo || '',
              notificationSent: responseData["Notification Sent"] || responseData.notificationSent || ''
            }];
          }
        }
      } catch (freshErr) {
        console.error('Error fetching fresh leads before campaign:', freshErr);
      }

      // Update UI immediately with fresh leads snapshot
      if (parsedLeads.length > 0) {
        setLeadsData(parsedLeads);
      }

      // Check if there are leads with 'New' status after fresh fetch
      const newLeads = parsedLeads.filter(lead => lead.status === 'New');
      if (newLeads.length === 0) {
        showStatus('Aucun prospect avec le statut "Nouveau" trouvé. Campagne échouée.', 'error');
        return;
      }

      console.log('Starting campaign with URL:', startCampaignWebhookUrl);
      
      const response = await fetch(startCampaignWebhookUrl, {
        method: 'GET',
        mode: 'cors',
      });
      
      console.log('Campaign response status:', response.status);
      console.log('Campaign response ok:', response.ok);
      
      if (response.ok) {
        const responseData = await response.json();
        console.log('Campaign response:', responseData);
        showStatus(`Campagne démarrée avec succès pour ${newLeads.length} prospect(s) avec le statut "Nouveau" !`);
        
        // Refetch all data after 4 seconds to allow lead status to change
        setTimeout(() => {
          fetchLeadsData();
        }, 4000);
      } else {
        const errorText = await response.text();
        console.error('Campaign server error:', errorText);
        showStatus(`Erreur lors du démarrage de la campagne: ${response.status}`, 'error');
      }
    } catch (error) {
      console.error('Error starting campaign:', error);
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        showStatus('Erreur réseau: Impossible de se connecter au serveur', 'error');
      } else {
        showStatus('Erreur lors du démarrage de la campagne', 'error');
      }
    }
  };

  // Manager functions removed in simplified flow


  return (
    <div className="control-panel">
      <h2 className="section-title">Agent de Réactivation ALMA</h2>
      
      
      <div className="workflow-intro">
        <p><strong>Workflow de démonstration :</strong> Suivez les étapes dans l'ordre pour configurer et lancer une campagne de test.</p>
      </div>

      {/* Warm Up WhatsApp Section */}
      <div className="workflow-section warmup-section">
        <div className="section-header">
          <h3 className="workflow-step">Étape 0: Warm up WhatsApp</h3>
        </div>
        <div className="warmup-content">
          <div className="warmup-message">
            <p><strong>Instructions :</strong> Pourriez-vous, s'il vous plaît, envoyer quelques messages au numéro d'ALMA :</p>
            <div className="phone-number">
              <strong>+1 555 162 6125</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Étape 1: Préparer le contact CRM démo */}
      <div className="workflow-section">
        <div className="section-header">
          <h3 className="workflow-step">Étape 1 — Préparer le test contact dans ImmoFacile</h3>
        </div>
        <div className="workflow-intro" style={{ textAlign: 'center' }}>
          <p>
            <strong>ALMA</strong> utilisera le contexte de la fiche de contact associée au numéro <strong>212624984514</strong> dans ImmoFacile. 
            Copiez le numéro ci-dessous et collez-le dans la barre de recherche d'ImmoFacile pour trouver le contact. 
            Vous pouvez modifier le nom, le contexte et le projet directement dans la fiche.
            <br />
            <strong style={{ color: '#e74c3c', backgroundColor: '#ffeaea', padding: '2px 4px', borderRadius: '3px' }}>Ne changez jamais le numéro du contact dans ImmoFacile.</strong>
          </p>
          <div style={{ 
            display: 'flex', 
            gap: 12, 
            alignItems: 'center', 
            justifyContent: 'center',
            marginTop: 16,
            padding: '16px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <code style={{ 
              padding: '8px 12px', 
              border: '1px solid #ccc', 
              borderRadius: 4,
              fontSize: '16px',
              fontWeight: '600',
              background: 'white'
            }}>
              212624984514
            </code>
            <button
              className="action-btn"
              onClick={() => { navigator.clipboard.writeText('212624984514'); showStatus('Numéro copié dans le presse‑papiers'); }}
              style={{ padding: '8px 16px' }}
            >
              <div className="btn-icon">📋</div>
              <div className="btn-text">Copier</div>
            </button>
          </div>
          <p style={{ marginTop: '8px', fontSize: '14px', color: '#666', textAlign: 'center' }}>
            Coller le numéro dans la barre de recherche
          </p>
        </div>
      </div>


      {/* Étape 2: Prospects */}
      <div className="workflow-section">
        <div className="section-header">
          <h3 className="workflow-step">Étape 2 — Prospects</h3>
          <button 
            className="refresh-data-btn" 
            onClick={() => {
              fetchLeadsData();
            }}
            disabled={dataLoading}
          >
            <div className="btn-icon">↻</div>
            <span>{dataLoading ? 'Actualisation...' : 'Actualiser'}</span>
          </button>
        </div>
        

        {dataLoading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Chargement des prospects...</p>
          </div>
        ) : (
          <div className="leads-table-container">
            <table className="leads-table">
              <thead>
                <tr>
                  <th>Téléphone</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {leadsData.length > 0 ? (
                  leadsData.map((lead, index) => (
                    <tr key={`${lead.phone}-${index}`} className={lead.status === 'New' ? 'new-lead' : ''}>
                      <td>{lead.phone}</td>
                      <td>
                        <span className={`status-badge ${lead.status.toLowerCase()}`}>
                          {getLocalizedStatus(lead.status)}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="no-data">Aucun prospect trouvé</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        
        <div className="leads-actions" style={{justifyContent: 'center'}}>
          <button className="action-btn scenario-btn" onClick={openContextLeadModal}>
            <div className="btn-icon">+</div>
            <div className="btn-text">Ajouter prospect</div>
          </button>
        </div>
      </div>

      {/* Campaign Section */}
      <div className="workflow-section">
        <div className="section-header">
          <h3 className="workflow-step">Campagne</h3>
        </div>
        <div className="button-group">
          <button 
            className="action-btn campaign-btn" 
            onClick={startCampaign}
            disabled={leadsData.filter(lead => lead.status === 'New').length === 0}
          >
            <div className="btn-icon">▶</div>
            <div className="btn-text">Démarrer Campagne</div>
            <div className="btn-description">Lancer Workflow</div>
          </button>
          <div className="button-description">
            {leadsData.filter(lead => lead.status === 'New').length === 0 ? (
              <p><strong>Nouveaux prospects requis :</strong> Vous devez avoir au moins un prospect avec le statut "Nouveau" pour lancer une campagne.</p>
            ) : (
              <div>
                <p><strong>Prêt à lancer :</strong> La campagne utilisera {leadsData.filter(lead => lead.status === 'New').length} nouveau(x) prospect(s) avec le statut "Nouveau".</p>
                {leadsData.filter(lead => lead.status === 'New').length > 0 && (
                  <div className="new-leads-list">
                    <p><strong>Nouveaux prospects :</strong></p>
                    <ul>
                      {leadsData.filter(lead => lead.status === 'New').map((lead, index) => (
                        <li key={index}>
                          {lead.phone}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contrôles */}
      <div className="workflow-section control-section">
        <div className="section-header">
          <h3 className="workflow-step">Contrôles</h3>
        </div>
        <div className="button-group">
          <button className="action-btn clear-btn" onClick={openClearMemoryModal}>
            <div className="btn-icon">↻</div>
            <div className="btn-text">Effacer Mémoire Agent</div>
            <div className="btn-description">Réinitialiser État Démo</div>
          </button>
          <div className="button-description">
            <p><strong>Réinitialisation :</strong> Supprime la mémoire de l'agent, la conversation du prospect et le prospect du CRM fictif.</p>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Traitement en cours...</p>
        </div>
      )}


      {/* Premium Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Effacer Mémoire Agent</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            
            <div className="modal-body">
              <p className="modal-description">
                Saisissez le numéro de téléphone pour effacer la mémoire de l'agent pour ce prospect.
              </p>
              
              <div className="input-group">
                <label htmlFor="phone-input" className="input-label">Numéro de Téléphone</label>
                <select
                  id="phone-input"
                  className="phone-input"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  disabled={dataLoading}
                >
                  <option value="">Sélectionnez un numéro...</option>
                  {leadsData.map((lead) => (
                    <option key={lead.phone} value={lead.phone}>
                      {lead.phone} {lead.name ? `(${lead.name})` : ''}
                    </option>
                  ))}
                </select>
                <small className="input-hint">Sélectionnez le numéro de téléphone du prospect à effacer (seuls les prospects disponibles sont affichés)</small>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="modal-btn secondary" onClick={closeModal}>
Annuler
              </button>
              <button 
                className="modal-btn primary" 
                onClick={clearAgentMemory}
                disabled={modalLoading || !phoneNumber.trim()}
              >
                {modalLoading ? (
                  <>
                    <div className="btn-spinner"></div>
                    Effacement...
                  </>
                ) : (
                  'Effacer Mémoire'
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Lead with Context Modal */}
      {showContextLeadModal && (
        <div className="modal-overlay" onClick={closeContextLeadModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Ajouter prospect</h3>
              <button className="modal-close" onClick={closeContextLeadModal}>×</button>
            </div>
            
            <div className="modal-body">
              <p className="modal-description">
                Saisissez les détails du prospect pour créer un nouveau prospect.
              </p>
              
              <div className="input-group">
                <label htmlFor="context-phone-input" className="input-label">Numéro de Téléphone *</label>
                <select
                  id="context-phone-input"
                  className="phone-input"
                  value={contextPhoneNumber}
                  onChange={(e) => setContextPhoneNumber(e.target.value)}
                  required
                >
                  <option value="">Sélectionnez un numéro...</option>
                  {allPhoneNumbers
                    .filter(phone => !leadsData.some(lead => lead.phone === phone))
                    .map((phone) => (
                      <option key={phone} value={phone}>
                        {phone}
                      </option>
                    ))}
                </select>
                <small className="input-hint">Requis - Sélectionnez le numéro de téléphone pour créer un prospect avec contexte (tous les numéros disponibles)</small>
              </div>


            </div>
            
            <div className="modal-footer">
              <button className="modal-btn secondary" onClick={closeContextLeadModal}>
Annuler
              </button>
              <button 
                className="modal-btn primary" 
                onClick={createContextLead}
                disabled={contextLeadLoading || !contextPhoneNumber.trim()}
              >
                {contextLeadLoading ? (
                  <>
                    <div className="btn-spinner"></div>
                    Création...
                  </>
                ) : (
                  'Ajouter prospect'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manager modal removed in simplified flow */}

      {/* Status Modal */}
      {showStatusModal && (
        <div className="status-modal-overlay">
          <div className="status-modal-content">
            <div className={`status-modal-icon ${statusType}`}>
              {statusType === 'success' ? '✓' : '✗'}
            </div>
            <div className="status-modal-message">
              {statusMessage}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlPanel;
